import { useCallback, useMemo, useState } from 'react';

import { checkGender } from '@psy/validations';

import { useLanguage } from '../../contexts/language';
import { useField, validateAll } from '../../hooks/field';
import { useUpdateProfile } from '../../hooks/update-profile';
import { useVisit } from '../../hooks/visit';

import { ChoiceList } from '../choice-list';
import { StepActions } from '../step-actions';

import styles from './style.module.css';

// Step 3: how the person describes their gender, required, with "Prefer not to
// say" as a full answer. It is a self-description, not the administrative sex
// a fiscal code encodes (O03).
export function GenderStep({ profile, onBack, onDone }) {
  const { text, translate } = useLanguage();
  const updateProfile = useUpdateProfile();
  const capture = useVisit();
  const gender = useField({ initial: profile.gender, check: checkGender });
  const [ busy, setBusy ] = useState(false);

  const options = useMemo(function() {
    return [ 'WOMAN', 'MAN', 'NON_BINARY', 'SELF_DESCRIBED', 'PREFER_NOT_TO_SAY' ].map(function(key) {
      return { key, label: text.genders[ key ] };
    });
  }, [ text ]);

  const onSubmit = useCallback(function(event) {
    event.preventDefault();

    if (validateAll([ gender ]) === false) {
      return null;
    }

    setBusy(true);

    const owns = capture();

    return updateProfile({ gender: gender.value }).then(function() {
      if (owns() === false) {
        return null;
      }

      return onDone();
    }).catch(function(failed) {
      if (owns() === false) {
        return;
      }

      setBusy(false);
      gender.refuse(failed.message);
    });
  }, [ gender, updateProfile, onDone, capture ]);

  return (
    <form className={ styles.form } onSubmit={ onSubmit } noValidate>
      <ChoiceList
        ref={ gender.ref }
        name='gender'
        legend={ text.genderTitle }
        intro={ text.genderIntro }
        options={ options }
        value={ gender.value }
        onChange={ gender.onChange }
        error={ translate(gender.error) }
      />

      <StepActions onBack={ onBack } submitLabel={ text.continue } busy={ busy }/>
    </form>
  );
}
